
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Therapist } from "../../../therapist";
import { updateTherapistProfile } from "../../application/therapistProfile";
import { useTherapist } from "../../../../context/useTherapist";
import { Globe } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";

interface SessionConfigCardProps {
    therapist: Therapist | null;
}

export default function SessionConfigCard({ therapist }: SessionConfigCardProps) {
    const { t } = useTranslation();
    const { setTherapist } = useTherapist();
    const [language, setLanguage] = useState(therapist?.language ?? "es");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (therapist?.language) {
            setLanguage(therapist.language);
        }
    }, [therapist]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!therapist) return;

        try {
            setLoading(true);
            // We only update the language field, following existing pattern in UserInfoCard
            // reusing the generic update method
            const updated = await updateTherapistProfile(therapist.id, {
                ...therapist, // Keep other fields same if API requires full object, usually PATCH is better but following UserInfoCard pattern
                language: language
            });

            setTherapist(updated);
            alert(t("common.success"));
        } catch (err) {
            console.error("Error updating session config", err);
            alert(t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <ComponentCard
            glass
            title={
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center shrink-0">
                        <Globe className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <span className="block text-lg font-bold text-content-primary truncate">
                            {t("profile.sessionConfig.notes")}
                        </span>
                        <p className="hidden sm:block text-xs text-content-tertiary font-normal">
                            {t("profile.sessionConfig.languageSelect")}
                        </p>
                    </div>
                </div>
            }
        >
            <form onSubmit={handleSave} className="space-y-6">
                <div className="max-w-md group">
                    <p className="sm:hidden text-sm text-content-tertiary mb-4 px-1">
                        {t("profile.sessionConfig.languageSelect")}
                    </p>
                    <select
                        className="w-full h-12 rounded-2xl border border-gray-100 bg-white/50 px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-white/[0.03] dark:border-white/5 dark:text-white transition-all shadow-theme-xs group-hover:border-gray-200 dark:group-hover:border-white/10"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                    >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                    </select>
                </div>

                <div className="flex justify-start">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="shadow-lg shadow-brand-500/20 px-8 py-3 font-bold"
                    >
                        {loading ? (
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {t("common.saving")}
                            </div>
                        ) : t("profile.sessionConfig.save")}
                    </Button>
                </div>
            </form>
        </ComponentCard>
    );
}
